import { useEffect, useRef, useCallback } from 'react'
import { Platform, PermissionsAndroid } from 'react-native'
import {
	InterfaceType,
	StarDeviceDiscoveryManager,
	StarDeviceDiscoveryManagerFactory,
	StarPrinter,
} from 'react-native-star-io10'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { setPersistedState } from '../../../state/Slices/persistedSlice'

const usePrinterConnection = () => {
	const dispatch = useAppDispatch()
	const { receiptPrinter, printerType } = useAppSelector(state => state.persisted)
	const managerRef = useRef<StarDeviceDiscoveryManager | undefined>()

	let EscPosPrinter = null
	if (Platform.OS !== 'web') {
		EscPosPrinter = require('react-native-esc-pos-printer')
	}

	const checkBluetoothPermissions = async () => {
		if (Platform.OS !== 'android') return true

		try {
			const nearbyDevices = await PermissionsAndroid.request(
				PermissionsAndroid.PERMISSIONS.BLUETOOTH_SCAN
			)
			const bluetoothConnect = await PermissionsAndroid.request(
				PermissionsAndroid.PERMISSIONS.BLUETOOTH_CONNECT
			)

			return (
				nearbyDevices === PermissionsAndroid.RESULTS.GRANTED &&
				bluetoothConnect === PermissionsAndroid.RESULTS.GRANTED
			)
		} catch (error) {
			console.error('Error checking permissions:', error)
			return false
		}
	}

	const findStarPrinters = useCallback(
		async (targetPrinter?: StarPrinter) => {
			try {
				await managerRef.current?.stopDiscovery()

				const hasPermissions = await checkBluetoothPermissions()
				if (!hasPermissions) {
					throw new Error('Bluetooth permissions denied')
				}

				if (Platform.OS === 'android') {
					try {
						managerRef.current = await StarDeviceDiscoveryManagerFactory.create([
							InterfaceType.BluetoothLE,
						])
					} catch (error) {
						if (error.message.includes('Not supported interface')) {
							managerRef.current = await StarDeviceDiscoveryManagerFactory.create([
								InterfaceType.Bluetooth,
							])
						} else {
							throw error
						}
					}
				} else {
					managerRef.current = await StarDeviceDiscoveryManagerFactory.create([
						InterfaceType.Bluetooth,
						InterfaceType.BluetoothLE,
					])
				}

				if (!managerRef.current) {
					throw new Error('Failed to initialize printer discovery manager')
				}

				managerRef.current.discoveryTime = 10000
				managerRef.current.onPrinterFound = (printer: StarPrinter) => {
					dispatch(
						setPersistedState({
							receiptPrinter: printer,
							printerType: 'star',
						})
					)
					console.log(`Found printer: ${printer.connectionSettings.identifier}.`)
				}

				managerRef.current.onDiscoveryFinished = () => {
					console.log('Discovery finished')
				}

				await managerRef.current.startDiscovery()
			} catch (error) {
				console.error('Star printer discovery error:', error)
			}
		},
		[dispatch]
	)

	const findEpsonPrinters = useCallback(
		async (targetPrinter?: any) => {
			if (EscPosPrinter === null) return
			try {
				const printers = await EscPosPrinter.discover()
				const printer = targetPrinter
					? printers.find(p => p.name === targetPrinter.name)
					: printers[0]

				if (printer) {
					dispatch(
						setPersistedState({
							receiptPrinter: printer,
							printerType: 'epson',
						})
					)
				} else if (!targetPrinter) {
					dispatch(
						setPersistedState({
							receiptPrinter: null,
							printerType: '',
						})
					)
				}
			} catch (error) {
				console.error('Epson printer discovery error:', error)
			}
		},
		[dispatch]
	)

	useEffect(() => {
		const initializePrinter = async () => {
			if (!receiptPrinter) return
			if (printerType === 'star') {
				await findStarPrinters(receiptPrinter)
			} else if (printerType === 'epson') {
				await findEpsonPrinters(receiptPrinter)
			}
		}

		initializePrinter()

		return () => {
			managerRef.current?.stopDiscovery()
		}
	}, [])

	return {
		findStarPrinters,
		findEpsonPrinters,
		checkBluetoothPermissions,
	}
}

export default usePrinterConnection
